import React from 'react';
import { AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react';

/**
 * Reusable Confirmation Modal Component
 * @param {boolean} show - Whether to show the modal
 * @param {function} onClose - Close modal callback
 * @param {function} onConfirm - Confirm action callback
 * @param {string} title - Modal title
 * @param {string} message - Modal message
 * @param {string} type - Modal type: 'info', 'warning', 'danger', 'success'
 * @param {string} confirmText - Confirm button text (default: 'Confirm')
 * @param {string} cancelText - Cancel button text (default: 'Cancel')
 */
export default function ConfirmationModal({
  show,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  type = 'info',
  confirmText = 'Confirm',
  cancelText = 'Cancel'
}) {
  if (!show) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle size={48} />;
      case 'warning':
        return <AlertTriangle size={48} />;
      case 'danger':
        return <AlertCircle size={48} />;
      default:
        return <Info size={48} />;
    }
  };

  const getColors = () => {
    switch (type) {
      case 'success':
        return {
          icon: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          button: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
        };
      case 'warning':
        return {
          icon: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          button: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
        };
      case 'danger':
        return {
          icon: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          button: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
        };
      default:
        return {
          icon: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
          button: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)'
        };
    }
  };

  const colors = getColors();

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        animation: 'fadeIn 0.2s ease-in'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '450px',
          width: '90%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          animation: 'slideUp 0.3s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: colors.icon,
          margin: '0 auto 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white'
        }}>
          {getIcon()}
        </div>

        {/* Title */}
        <h3 style={{
          margin: '0 0 12px 0',
          fontSize: '20px',
          fontWeight: '600',
          color: '#111827',
          textAlign: 'center'
        }}>
          {title}
        </h3>

        {/* Message */}
        <p style={{
          margin: '0 0 24px 0',
          fontSize: '15px',
          color: '#6b7280',
          textAlign: 'center',
          lineHeight: '1.6'
        }}>
          {message}
        </p>

        {/* Buttons */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px',
              background: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#e5e7eb';
              e.target.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#f3f4f6';
              e.target.style.transform = 'scale(1)';
            }}
          >
            {cancelText}
          </button>
          
          <button
            onClick={handleConfirm}
            style={{
              padding: '12px 24px',
              background: colors.button,
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
